import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ReportsService } from '@app/modules/reports/services/reports.service';
import { SetReportsAction } from '@app/modules/reports/store/reports.actions';
import { Report } from '@app/modules/reports/types/report.types';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { map, switchMap } from 'rxjs';

@Component({
  selector: 'app-report-search',
  imports: [FormsModule, ReactiveFormsModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './report-search.component.html',
})
export class ReportSearchComponent {
  private dialogRef = inject(NgbActiveModal);
  private store = inject(Store);
  private toastService = inject(ToastService);
  private reportsService = inject(ReportsService);
  private reportsUtilsService = inject(ReportsUtilsService);
  private translateService = inject(TranslateService);

  protected kitTokenControl = new FormControl<string>('', [
    Validators.required,
  ]);
  protected loading = signal(false);

  protected search() {
    if (this.kitTokenControl.invalid) {
      return;
    }

    this.loading.set(true);

    this.reportsService
      .listReports(
        this.reportsUtilsService.buildHttpParams({
          kitIdToken: this.kitTokenControl.value,
        }),
        {}
      )
      .pipe(
        switchMap((result) =>
          this.store
            .dispatch(new SetReportsAction(result.items))
            .pipe(map(() => result))
        )
      )
      .subscribe({
        next: (result) => {
          const reports = result.items;
          if (reports.length === 1) {
            this.reportsUtilsService.openReportDetailsDialog(reports[0].id);
            this.dismiss();
          } else if (reports.length > 1 && reports.length < 100) {
            this.reportsUtilsService.openReportSearchResultsDialog(
              reports.map((report: Report) => report.id)
            );
            this.dismiss();
          } else if (reports.length >= 100) {
            this.toastService.danger(
              this.translateService.instant(
                'reports.search.feedback.tooManyResults'
              ),
              this.translateService.instant(
                'reports.search.feedback.tooManyResultsTitle'
              ),
              5000
            );
          } else {
            this.toastService.danger(
              this.translateService.instant('reports.search.feedback.notFound'),
              this.translateService.instant(
                'reports.search.feedback.notFoundTitle'
              ),
              5000
            );
          }
          this.loading.set(false);
        },
        error: () => {
          this.toastService.danger(
            this.translateService.instant('reports.search.feedback.notFound'),
            this.translateService.instant(
              'reports.search.feedback.notFoundTitle'
            ),
            5000
          );
          this.loading.set(false);
        },
      });
  }

  dismiss() {
    this.dialogRef.close();
  }
}
